import { getCurrentDateLocal } from '@/utils/common';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  LessonSchedule,
  ViewType,
  Lesson,
  DailyRosterInstructor,
  HoverState
} from '@/constants/Interfaces/CommonInterfaces';

export type DailyRosterStoreState = {
  // Data
  lessonSchedule: LessonSchedule[];
  selectedLesson: Lesson | null;
  deletedRecord: Record<string, unknown> | null;

  // UI State
  selectedDate: string | null;
  selectedType: ViewType;
  showUndoModal: boolean;
  isLoading: boolean;
  error: string | null;

  // Hover State for Lesson Details Popup
  hoverState: HoverState;

  // Actions
  setLessonSchedule: (
    data: LessonSchedule[] | ((prev: LessonSchedule[]) => LessonSchedule[])
  ) => void;
  setDeletedRecord: (data: Record<string, unknown> | null) => void;
  setSelectedLesson: (lesson: Lesson | null) => void;
  setSelectedDate: (date: string | null) => void;
  setSelectedType: (type: ViewType) => void;
  setShowUndoModal: (show: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Hover Actions
  showLessonDetails: (
    lesson: Lesson,
    instructor: DailyRosterInstructor,
    position: { x: number; y: number }
  ) => void;
  hideLessonDetails: () => void;
  setPopupHovering: (hovering: boolean) => void;

  resetStore: () => void;
};

type DailyRosterDataState = {
  lessonSchedule: LessonSchedule[];
  selectedLesson: Lesson | null;
  deletedRecord: Record<string, unknown> | null;
  selectedDate: string | null;
  selectedType: ViewType;
  showUndoModal: boolean;
  isLoading: boolean;
  error: string | null;
  hoverState: HoverState;
};

const initialState: DailyRosterDataState = {
  lessonSchedule: [],
  selectedLesson: null,
  selectedDate: getCurrentDateLocal(),
  deletedRecord: null,
  selectedType: 'Day',
  showUndoModal: false,
  isLoading: false,
  error: null,
  hoverState: {
    isVisible: false,
    lesson: null,
    instructor: null,
    position: { x: 0, y: 0 },
    isPopupHovering: false
  }
};

const useDailyRosterStore = create<DailyRosterStoreState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setLessonSchedule: (
        data: LessonSchedule[] | ((prev: LessonSchedule[]) => LessonSchedule[])
      ) => {
        if (typeof data === 'function') {
          set(state => ({ lessonSchedule: data(state.lessonSchedule) }));
        } else {
          set({ lessonSchedule: data });
        }
      },

      setSelectedLesson: (lesson: Lesson | null) =>
        set({ selectedLesson: lesson }),

      setDeletedRecord: (data: Record<string, unknown> | null) =>
        set({ deletedRecord: data }),

      setSelectedDate: (date: string | null) => set({ selectedDate: date }),

      setSelectedType: (type: ViewType) => set({ selectedType: type }),

      setShowUndoModal: (show: boolean) => set({ showUndoModal: show }),

      setIsLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => set({ error }),

      showLessonDetails: (
        lesson: Lesson,
        instructor: DailyRosterInstructor,
        position: { x: number; y: number }
      ) => {
        // Only update if the state is actually different
        const currentState = get();
        const isSameLesson =
          currentState.hoverState.isVisible &&
          currentState.hoverState.lesson?.client_name === lesson.client_name &&
          currentState.hoverState.lesson?.start === lesson.start &&
          currentState.hoverState.instructor?.name === instructor.name;

        if (!isSameLesson) {
          set({
            hoverState: {
              isVisible: true,
              lesson,
              instructor,
              position,
              isPopupHovering: false
            }
          });
        }
      },

      hideLessonDetails: () => {
        // Only update if currently visible and not hovering the popup
        const currentState = get();
        if (
          currentState.hoverState.isVisible &&
          !currentState.hoverState.isPopupHovering
        ) {
          set({
            hoverState: {
              isVisible: false,
              lesson: null,
              instructor: null,
              position: { x: 0, y: 0 },
              isPopupHovering: false
            }
          });
        }
      },

      setPopupHovering: (hovering: boolean) => {
        const currentState = get();
        if (currentState.hoverState.isPopupHovering !== hovering) {
          set({
            hoverState: {
              ...currentState.hoverState,
              isPopupHovering: hovering
            }
          });
        }
      },

      resetStore: () => set(initialState)
    }),
    {
      name: 'daily-roster-store'
    }
  )
);

export default useDailyRosterStore;
